import { Flex, Box, Button, Text, Image, Badge, Menu, MenuButton, MenuList, MenuItem } from '@chakra-ui/react';
import { TFunction } from 'i18next';
import { CellValue } from 'react-table';

import FallbackAvatar from 'components/FallbackAvatar';
import { IndeterminateCheckbox } from 'components/Table';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { formatPhoneNum } from 'utils/helpers';

export const getEmployeesCommonColumns = (loggedInUserInfo: any, t: TFunction, navigate: any) => [
  ...(loggedInUserInfo?.roleId === roles.ADMIN
    ? [
        {
          id: 'selection',
          width: 30,
          Header: ({ getToggleAllRowsSelectedProps }: any) => {
            return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
          },
          Cell: ({ row }: any) => {
            return loggedInUserInfo?.userId !== row.original?.userId ? (
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />
            ) : null;
          },
        },
      ]
    : []),
  {
    Header: t('common:name'),
    accessor: 'firstName',
    width: 220,
    enableSort: true,
    Cell: (data: CellValue) => {
      return (
        <Flex alignItems="center">
          <Image
            src={data.row.original?.employee_image}
            fallback={<FallbackAvatar name={data.value + ' ' + data.row.original?.lastName} />}
            alt={data.value + ' ' + data.row.original?.lastName}
            boxSize="40px"
            objectFit="cover"
            borderRadius="lg"
          />
          <Box ml="4">
            <Text fontWeight={700} color="newGray.1000" mb="1" data-testid="user-name">
              {`${data.row.original?.firstName} ${data.row.original?.lastName}`}
            </Text>
            <Text mb="1" color="gray.500" data-testid="user-role">
              {data.row.original?.roleName}
            </Text>
            {data.row.original?.is_register ? null : (
              <Badge
                alignSelf="flex-start"
                variant="solid"
                bg="secondary.400"
                color="white"
                px="2"
                py="0.5"
                fontWeight="normal"
                textTransform="uppercase"
                borderRadius="8px"
                data-testid="invite-pending"
              >
                {t('employees:invitePending')}
              </Badge>
            )}
          </Box>
        </Flex>
      );
    },
  },
  {
    Header: t('employees:contactInfo'),
    accessor: 'contactInfo',
    enableSort: false,
    width: 200,
    Cell: (data: CellValue) => {
      const phoneNum = data.row.original?.phone;
      let formattedPhoneNum = '-----';
      if (phoneNum) {
        formattedPhoneNum = formatPhoneNum(phoneNum);
      }
      return (
        <Box>
          <Text fontWeight="semibold" data-testid="user-email">
            {data.row.original?.email}
          </Text>
          <Text fontWeight={300} color="gray.500" data-testid="user-phone">
            {formattedPhoneNum}
          </Text>
        </Box>
      );
    },
  },
  {
    Header: t('common:groups'),
    accessor: 'groups',
    width: 220,
    Cell: (data: CellValue) => {
      return (
        <Menu data-testid="user-group">
          <MenuButton as={Button} variant="unstyled">
            <Text fontWeight={700} color="primary.500" data-testid="user-group">
              {Array.isArray(data.value) && data.value.length
                ? data.value.length === 1
                  ? `${data.value[0]?.name} `
                  : `${data.value[0]?.name} and ${data.value.length - 1} others`
                : '-----'}
            </Text>
          </MenuButton>
          {data.value.length > 0 ? (
            <MenuList>
              {data.value.map((item: any, index: number) => (
                <MenuItem
                  color="primary.500"
                  fontWeight="600"
                  onClick={() => navigate(routePaths.employees.employeeGroupDetail.replace(':id', item?.id))}
                  key={index}
                >
                  {item?.name}
                </MenuItem>
              ))}
            </MenuList>
          ) : null}
        </Menu>
      );
    },
  },
  {
    Header: t('employees:trackMileage'),
    accessor: 'mileageTracking',
    width: 130,
    Cell: (data: CellValue) => {
      return data.value ? (
        <Text color="success.500" fontWeight="semibold" data-testid="user-trackMileage">
          {t('common:yes')}
        </Text>
      ) : (
        <Text color="secondary.700" fontWeight="semibold" data-testid="user-trackMileage">
          {t('common:no')}
        </Text>
      );
    },
  },
];
